/**
 * Regras do Acompanhar entrega (`track`). Puro: quem chama passa o config
 * e, quando precisa de nome, o catálogo.
 *
 * - A saída é escolhida ao criar a regra e não muda depois.
 * - Só Parado aceita várias regras (cada uma com o seu tempo). Falha e
 *   Sucesso têm no máximo uma cada.
 * - Um status só pode estar em uma regra. Status pai e sub-status se
 *   sobrepõem: `CANCELED` numa regra ocupa `CANCELED:INTERNAL` nas outras.
 */
import { nanoid } from 'nanoid';
import type { WorkflowCatalog } from '../api/types';
import type { TrackDeliveryConfig, TrackOutput, TrackRule } from '../types';
import { catalogNode, type PortVariant } from './catalog';
import { formatMinutes } from './node-titles';

/** Prazo máximo: padrão e teto de 72 h, como no back. */
export const TRACK_MAX_MINUTES = 72 * 60;

/** Tempo de uma regra de Parado nova. */
export const TRACK_DEFAULT_STUCK_MINUTES = 30;

/** Ordem das saídas no menu "Adicionar regra" e no card. */
export const TRACK_OUTPUTS: TrackOutput[] = ['stuck', 'failed', 'out'];

const FALLBACK_LABEL: Record<TrackOutput, string> = {
  stuck: 'Parado',
  failed: 'Falha',
  out: 'Sucesso',
};

const FALLBACK_VARIANT: Record<TrackOutput, PortVariant> = {
  stuck: 'alt',
  failed: 'fail',
  out: 'default',
};

const VARIANTS: ReadonlySet<string> = new Set([
  'default',
  'alt',
  'fail',
  'muted',
]);

function catalogPort(catalog: WorkflowCatalog, output: TrackOutput) {
  const spec = catalogNode(catalog, 'track')?.ports;
  return spec && spec.type !== 'fromList'
    ? spec.items.find((p) => p.id === output)
    : undefined;
}

/** Nome da saída (o mesmo da pill do card). */
export const trackOutputLabel = (
  catalog: WorkflowCatalog,
  output: TrackOutput,
): string => catalogPort(catalog, output)?.label || FALLBACK_LABEL[output];

/** Cor da saída (a mesma da pill do card). */
export function trackOutputVariant(
  catalog: WorkflowCatalog,
  output: TrackOutput,
): PortVariant {
  const v = catalogPort(catalog, output)?.variant;
  return v && VARIANTS.has(v) ? (v as PortVariant) : FALLBACK_VARIANT[output];
}

/** Só Parado aceita mais de uma regra. */
export const canAddOutput = (
  config: Pick<TrackDeliveryConfig, 'rules'>,
  output: TrackOutput,
): boolean =>
  output === 'stuck' || !config.rules.some((r) => r.output === output);

/** Regra nova, ainda sem status. Parado nasce com o tempo padrão. */
export function newRule(
  output: TrackOutput,
  makeId: () => string = () => `r_${nanoid(6)}`,
): TrackRule {
  return output === 'stuck'
    ? {
        id: makeId(),
        output,
        statuses: [],
        minutes: TRACK_DEFAULT_STUCK_MINUTES,
      }
    : { id: makeId(), output, statuses: [] };
}

/** "Regra N": a posição na lista, começando em 1. Fora da lista, 0. */
export const ruleNumber = (
  config: Pick<TrackDeliveryConfig, 'rules'>,
  ruleId: string,
): number => config.rules.findIndex((r) => r.id === ruleId) + 1;

/**
 * Status já usados por outras regras (todas menos `exceptRuleId`), com o
 * número da regra que usa cada um.
 */
export function usedStatusesByRule(
  config: Pick<TrackDeliveryConfig, 'rules'>,
  exceptRuleId?: string,
): Map<string, number> {
  const used = new Map<string, number>();
  config.rules.forEach((rule, i) => {
    if (rule.id === exceptRuleId) return;
    for (const code of rule.statuses)
      if (!used.has(code)) used.set(code, i + 1);
  });
  return used;
}

/**
 * Regra que já ocupa `code`, contando a sobreposição de pai e sub-status:
 * `CANCELED` ocupa `CANCELED:X`, e `CANCELED:X` ocupa `CANCELED`.
 */
export function takenByRule(
  used: ReadonlyMap<string, number>,
  code: string,
): number | undefined {
  const direct = used.get(code);
  if (direct !== undefined) return direct;
  const [parent, sub] = code.split(':');
  if (sub !== undefined) return used.get(parent);
  for (const [other, n] of used) if (other.startsWith(`${code}:`)) return n;
  return undefined;
}

/**
 * Lista de status da regra sem repetição e sem sobreposição: com o status
 * pai escolhido, os sub-status dele saem (o pai já cobre).
 */
export function normalizeStatuses(statuses: string[]): string[] {
  const set = new Set(statuses);
  return [...set].filter((code) => {
    const [parent, sub] = code.split(':');
    return sub === undefined || !set.has(parent);
  });
}

/** "Aguardando transportadora +2": o primeiro status e quantos mais. */
export function firstWithMore(
  statuses: string[],
  labelOf: (code: string) => string,
): string {
  if (statuses.length === 0) return '';
  const more = statuses.length - 1;
  return more > 0 ? `${labelOf(statuses[0])} +${more}` : labelOf(statuses[0]);
}

/** Pedaço da condição da regra; `strong` é o que a pessoa escolheu. */
export interface SentencePart {
  text: string;
  strong?: boolean;
}

/**
 * Condição da regra por extenso, para o cartão e a janela de status: "Se
 * ficar em A ou B por mais de 20 min." Até `maxNames` nomes, depois "e
 * mais N". Regra sem status não tem frase.
 */
export function ruleSentence(
  rule: TrackRule,
  labelOf: (code: string) => string,
  maxNames = 3,
): SentencePart[] {
  if (rule.statuses.length === 0) return [];
  const minutes = rule.output === 'stuck' ? (rule.minutes ?? 0) : 0;
  const names = rule.statuses.slice(0, maxNames).map(labelOf);
  const rest = rule.statuses.length - names.length;
  const parts: SentencePart[] = [
    { text: minutes > 0 ? 'Se ficar em ' : 'Se entrar em ' },
  ];
  names.forEach((name, i) => {
    if (i > 0) {
      const last = rest === 0 && i === names.length - 1;
      parts.push({ text: last ? ' ou ' : ', ' });
    }
    parts.push({ text: name, strong: true });
  });
  if (rest > 0) parts.push({ text: ` e mais ${rest}` });
  if (minutes > 0) {
    parts.push(
      { text: ' por mais de ' },
      { text: formatMinutes(minutes), strong: true },
    );
  }
  parts.push({ text: '.' });
  return parts;
}

/** Frase da regra, para o subtítulo da janela de status. */
export function ruleDescription(rule: TrackRule, outputLabel: string): string {
  if (rule.output === 'stuck') {
    const minutes = rule.minutes ?? 0;
    return minutes > 0
      ? `Pedidos que ficarem nestes status por mais de ${formatMinutes(
          minutes,
        )} saem por ${outputLabel}.`
      : `Pedidos que entrarem nestes status saem por ${outputLabel} na hora.`;
  }
  return `Pedidos que entrarem nestes status saem por ${outputLabel}.`;
}

/** Prazo máximo entre 1 minuto e 72 h, inteiro. */
export const clampMaxMinutes = (minutes: number): number =>
  Math.min(TRACK_MAX_MINUTES, Math.max(1, Math.round(minutes)));

/** Tempo do Parado entre 0 e o prazo máximo, inteiro. */
export const clampStuckMinutes = (minutes: number, maxMinutes: number) =>
  Math.min(Math.max(0, maxMinutes), Math.max(0, Math.round(minutes)));
